<rt-dialog-header [heading]="heading" [subheading]="subheading" />

<rt-dialog-body layout-vertical>
  <rt-box variant="default no-padding background" layout-vertical>
    @if (locationsLoaded) {
      <div class="top-wrap">
        <ng-container *ngTemplateOutlet="top" />
      </div>
      <ng-container
        *ngTemplateOutlet="!locationGroups.length && queryControl.value ? noSearchResults : list"
      />
    } @else {
      <rt-spinner margin-large />
    }
  </rt-box>
  @if (hasAddNewLocation) {
    <rt-button (click)="addNewLocation()" class="optional" margin-top>
      <rt-icon name="add" />
      {{ 'app.locations.assign_locations_dialog.add_new_location' | translate }}
    </rt-button>
  }
</rt-dialog-body>

<rt-dialog-footer [hasOptionalActions]="true">
  @if (hasGoToSettings) {
    <rt-button (click)="goToLocationsSettings()" variant="plain" class="optional">
      <rt-icon name="settings" />
      {{ 'app.locations.assign_locations_dialog.manage_locations' | translate }}
    </rt-button>
  }
  @if (goBackHandler) {
    <rt-button class="optional" (click)="goBack()" variant="plain">
      <rt-icon name="arrow-back" />
      {{ 'app.common.go_back' | translate }}
    </rt-button>
  }
  @if (hasSkip) {
    <rt-button (click)="skip()" variant="plain">
      {{ 'app.common.skip' | translate }}
    </rt-button>
  }
  @if (!hasSkip) {
    <rt-button (click)="cancel()" variant="plain">
      {{ 'app.common.cancel' | translate }}
    </rt-button>
  }
  <rt-button
    (click)="save()"
    [disabled]="isConfirmDisabled()"
    [loading]="isLoading"
    variant="primary"
  >
    {{ submitLabel }}
  </rt-button>
</rt-dialog-footer>

<ng-template #list>
  @if (locationGroups.length) {
    <div [class.small]="hasAddNewLocation" class="scroll-wrap">
      @for (
        locationGroup of locationGroups;
        track trackByKey($index, locationGroup);
        let lastGroup = $last
      ) {
        @if (locationGroup.kind === TemplateKind.Group) {
          <div class="group-title" layout layout-align-items-center>
            <rt-heading variant="subheading" margin-right-small-xx>
              {{ locationGroup.title }}
            </rt-heading>
            <span>({{ locationGroup.locations.length }})</span>
          </div>
          <rt-divider />
        }
        @for (
          item of locationGroup.locations;
          track trackById($index, item);
          let lastLocation = $last
        ) {
          <div (click)="selectItem(item)" [class.checked]="isSelected(item)" class="item">
            @if (isSingleSelectMode) {
              <rt-radio
                [checked]="isSelected(item)"
                [hasLensIcon]="true"
                [hasNoTBMargin]="true"
                [hasAlignItemsStart]="true"
                (click)="$event.stopPropagation(); selectItem(item)"
                margin-top-small-xxx
              />
            }
            <div
              [rtTooltip]="getLocationItemTooltip(item)"
              [rtTooltipEnabled]="isLocationItemDisabled(item)"
            >
              @if (isMultiSelectMode) {
                <rt-checkbox
                  [checked]="isSelected(item)"
                  [disabled]="isLocationItemDisabled(item)"
                  (click)="$event.stopPropagation(); selectItem(item)"
                />
              }
            </div>
            <div class="item-content" [class.faded]="item.isArchived || item.isRemoved">
              <div class="title">
                <span
                  class="title-name"
                  [attr.margin-right-small-x]="!(item.isArchived || item.isRemoved) || null"
                  >{{ item.name }}</span
                >
                @if (item.isArchived) {
                  <rt-badge
                    [text]="'app.locations.archived' | translate"
                    color="secondary"
                    [iconName]="'archive'"
                    [attr.margin-right-small-x]="!item.isValid || null"
                    margin-left-small-xx
                  />
                }
                @if (item.isRemoved) {
                  <rt-badge
                    [text]="'app.locations.assign_locations_dialog.removed' | translate"
                    color="secondary"
                    [iconName]="'info'"
                    [attr.margin-right-small-x]="!item.isValid || null"
                    margin-left-small-xx
                  />
                }
                @if (!item.isValid) {
                  <span class="invalid">
                    <rt-icon
                      [name]="'error-outline'"
                      colorToken="colorIconCriticalDefault"
                      margin-left-small-xx
                      margin-right-small-xx
                    />
                    {{ 'app.locations.invalid_address' | translate }}
                  </span>
                }
              </div>
              <div class="description">
                {{ item.fullAddress }}
              </div>
            </div>
          </div>
          @if (!(lastGroup && lastLocation)) {
            <rt-divider />
          }
        }
      }
    </div>
  } @else {
    <rt-box [variant]="'default'" layout-vertical margin-medium margin-top-small-x>
      <rt-empty-state
        [icon]="'domain'"
        [title]="'app.locations.assign_locations_dialog.empty_state.title' | translate"
        [desc]="'app.locations.assign_locations_dialog.empty_state.description' | translate"
        padding-tb
      />
    </rt-box>
  }
</ng-template>

<ng-template #top>
  <rt-form-field>
    <rt-input
      [formControl]="queryControl"
      [hasClearButton]="true"
      [placeholder]="'app.common.placeholders.search.location.other' | translate"
      [autoFocus]="true"
      (reset)="queryControl.setValue('')"
    >
      <rt-icon prefix name="search" />
    </rt-input>
  </rt-form-field>
  <div class="list-heading">
    <span class="title">{{ 'app.common.locations' | translate }}</span>
    <rt-button
      #sortDropdownOrigin="rtOverlayOrigin"
      variant="plain"
      [dropdownOpened]="sortDropdownOpened"
      (click)="toggleSortDropdown()"
      rt-overlay-origin
      padding-right-small-x
    >
      <rt-icon name="sort" margin-right-small-xx />
      <span>{{ sortLabelTranslations[(currentSort$ | async)!] }}</span>
    </rt-button>
    <ng-template
      #sortDropdown="rtConnectedOverlay"
      [origin]="sortDropdownOrigin"
      [open]="sortDropdownOpened"
      (backdropClick)="toggleSortDropdown()"
      rt-connected-overlay
      hasBackdrop
    >
      <rt-dropdown [width]="DropdownWidth.SmallX">
        <rt-dropdown-header [fullWidthTitle]="true">
          {{ 'app.locations.sort_by.dropdown_title' | translate }}
        </rt-dropdown-header>
        @for (sortOption of sortOptionsToArray(); track trackByValue($index, sortOption)) {
          <rt-dropdown-item (click)="changeSortOption(sortOption); toggleSortDropdown()">
            {{ sortLabelTranslations[sortOption] }}
          </rt-dropdown-item>
        }
      </rt-dropdown>
    </ng-template>
    @if (isMultiSelectMode && isAllOptionAvailable) {
      <rt-button variant="plain" (click)="selectAll()" padding-right-small-x>
        {{ 'app.common.all' | translate }}
      </rt-button>
    }
    @if (isMultiSelectMode) {
      <rt-button variant="plain" (click)="selectNone()" padding-right-small-x>
        {{ 'app.common.none' | translate }}
      </rt-button>
    }
  </div>
</ng-template>

<ng-template #noSearchResults>
  <rt-box [variant]="'default'" layout-vertical margin-medium margin-top-small-x>
    <rt-empty-state
      [icon]="'error-outline'"
      [title]="'app.common.no_results_found' | translate"
      padding-tb
    >
      <rt-button variant="tertiary" (click)="queryControl.setValue('')">
        {{ 'app.common.clear_search' | translate }}
      </rt-button>
    </rt-empty-state>
  </rt-box>
</ng-template>

<ng-template #loading>
  <rt-spinner margin-large />
</ng-template>
